import React, { useEffect, useRef, useCallback, useState } from 'react';
import { ApiReferenceReact } from '@scalar/api-reference-react';
import '@scalar/api-reference-react/style.css';
import { Box } from '@availity/element';
import Button from '../../Utils/components/Button';
import moonSvg from '../../../static/assets/icons/toggle-moon.svg';
import sunSvg from '../../../static/assets/icons/toggle-sun.svg';
import GenerateDemoKeyModal from './GenerateDemoKeyModal';

interface ApiReferenceTabProps {
  apiSpec: unknown;
  theme: 'light' | 'dark';
  onToggleTheme: () => void;
}

const TOGGLE_ID = 'scalar-theme-toggle';

const getToggleSvg = (currentTheme: 'light' | 'dark') => {
  const src = currentTheme === 'dark' ? moonSvg : sunSvg;
  return `<img src="${src}" alt="Toggle theme" width="36" height="24" />`;
};

const expandNestedSchemaFromHash = () => {
  const hash = decodeURIComponent(window.location.hash.slice(1));
  if (!hash) return;

  const schemaParamMatch = hash.match(/(.*)(\\.body\\.|\\.path\\.|\\.query\\.|\\.header\\.)(.*)/);
  if (!schemaParamMatch) return;

  const nestedParam = schemaParamMatch[3];
  if (!nestedParam) return;

  const existingTarget = document.querySelector(`[id*="${nestedParam}"]`);
  if (existingTarget) return;

  const disclosureButtons = document.querySelectorAll('.schema-card-title--compact');
  for (const btn of disclosureButtons) {
    const el = btn as HTMLElement;
    if (el.textContent?.includes('Show additional properties') && el.offsetParent !== null) {
      el.click();
    }
  }

  const tryScrollToTarget = (attempts: number) => {
    if (attempts <= 0) return;

    const allIds = document.querySelectorAll(`[id*="${nestedParam}"]`);
    const target = [...allIds].find((el) => {
      const id = el.getAttribute('id') || '';
      return id.endsWith(nestedParam) || id.includes(`.${nestedParam}`);
    });

    if (target) {
      target.scrollIntoView({ block: 'start' });
    } else {
      requestAnimationFrame(() => tryScrollToTarget(attempts - 1));
    }
  };

  setTimeout(() => tryScrollToTarget(30), 200);
};

const ApiReferenceTab = ({ apiSpec, theme, onToggleTheme }: ApiReferenceTabProps): JSX.Element => {
  const containerRef = useRef<HTMLDivElement>(null);
  const callbackRef = useRef(onToggleTheme);
  const themeRef = useRef(theme);
  callbackRef.current = onToggleTheme;
  themeRef.current = theme;

  const [modalOpen, setModalOpen] = useState(false);
  const isDark = theme === 'dark';

  const handleLoaded = useCallback(() => {
    setTimeout(expandNestedSchemaFromHash, 500);
  }, []);

  useEffect(() => {
    const container = containerRef.current;
    let observer: MutationObserver | null = null;

    const inject = () => {
      if (!container) return;
      const headerSlot = container.querySelector('.t-doc__header .flex.h-6');
      if (!headerSlot) return;
      if (container.querySelector(`#${TOGGLE_ID}`)) return;

      observer?.disconnect();

      const btn = document.createElement('button');
      btn.id = TOGGLE_ID;
      btn.type = 'button';
      btn.setAttribute('aria-label', 'Toggle theme');
      btn.style.cssText = 'background:none;border:none;padding:0;cursor:pointer;display:flex;align-items:center;';
      btn.innerHTML = getToggleSvg(themeRef.current);
      btn.addEventListener('click', () => callbackRef.current());

      headerSlot.appendChild(btn);

      observer?.observe(container, { childList: true, subtree: true });
    };

    inject();
    observer = new MutationObserver(inject);
    if (container) observer.observe(container, { childList: true, subtree: true });

    return () => observer?.disconnect();
  }, []);

  useEffect(() => {
    const btn = containerRef.current?.querySelector(`#${TOGGLE_ID}`);
    if (btn) btn.innerHTML = getToggleSvg(theme);
  }, [theme]);

  useEffect(() => {
    const onHashChange = () => {
      setTimeout(expandNestedSchemaFromHash, 300);
    };
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);

  return (
    <div
      ref={containerRef}
      data-theme={theme}
      className={isDark ? 'dark-mode' : 'light-mode'}
      style={{ position: 'relative' }}
    >
      <Box sx={{ position: 'absolute', top: { xs: 56, md: 16 }, right: 24, zIndex: 100 }}>
        <Button variant="navbar-signup" onClick={() => setModalOpen(true)} sx={{ whiteSpace: 'nowrap' }}>
          Generate Demo Key
        </Button>
      </Box>
      <ApiReferenceReact
        configuration={{
          spec: { content: apiSpec },
          hideClientButton: true,
          hideModels: true,
          hideDarkModeToggle: true,
          showDeveloperTools: 'never',
          layout: 'modern',
          theme: 'default',
          onLoaded: handleLoaded,
          agent: {
            disabled: true,
          },
          authentication: {
            preferredSecurityScheme: 'apiKey',
          },
          customCss: `
          .ascii-art { display: none !important; }
          .darklight-reference { display: none !important; }
          .scalar-reference-intro-clients { display: none !important; }
          [id$="-group-2"] { display: none !important; }
          .light-mode {
            --scalar-color-1: #000C30;
            --scalar-color-2: rgba(17, 29, 66, 0.9);
            --scalar-color-3: rgba(0, 12, 48, 0.75);
            --scalar-color-accent: #519EE6;
            --scalar-background-1: #FFFFFF;
            --scalar-background-2: #EFF3FF;
            --scalar-background-3: #F7F9FF;
            --scalar-background-accent: #EFF3FF;
            --scalar-border-color: #C3CAE2;
          }
          .dark-mode {
            --scalar-color-1: #EFF3FF;
            --scalar-color-2: rgba(239, 243, 255, 0.9);
            --scalar-color-3: #BBD5EA;
            --scalar-color-accent: #8ab4f8;
            --scalar-background-1: #151823;
            --scalar-background-2: #191F31;
            --scalar-background-3: #151823;
            --scalar-background-accent: #2F3444;
            --scalar-border-color: #363C4D;
          }
          .light-mode label,
          .light-mode .scalar-card-header,
          .light-mode .scalar-card-header-title {
            color: var(--scalar-color-1) !important;
          }
          .dark-mode label,
          .dark-mode .scalar-card-header,
          .dark-mode .scalar-card-header-title {
            color: var(--scalar-color-1) !important;
          }
        `,
        }}
      />
      <GenerateDemoKeyModal open={modalOpen} onClose={() => setModalOpen(false)} theme={theme} />
    </div>
  );
};

export default ApiReferenceTab;
